import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  Dictionary,
  EntityId,
  EntityState,
} from '@reduxjs/toolkit';
import { apiClient } from 'api';
import {
  CreateOrganizationResourceInputData,
  CreateOrganizationResourceOutputData,
  CreateOrganizationResourceTransferData,
  CreateOrganizationTrayInputData,
  CreateOrganizationTrayOutputData,
  CreateOrganizationTrayTransferData,
  CreateOrganizationVarietyInputData,
  CreateOrganizationVarietyOutputData,
  CreateOrganizationVarietyTransferData,
  GetOrganizationResourceInventorySummaryList,
  GetOrganizationSeedBatchesList,
  GetOrganizationSeedInventorySummaryList,
  GetOrganizationTrayInventorySummaryList,
} from 'api/organization';
import {
  ResourceInventorySummary,
  Organization,
  TrayInventorySummary,
  SeedInventorySummary,
  SeedBatch,
} from 'constants/interface';
import { RootState } from 'state';

const organizationEntityAdapter = createEntityAdapter<Organization>();
const batchesEntityAdapter = createEntityAdapter<SeedBatch>();

export interface OrganizationResourceInventoriesState {
  isLoading: boolean
  remoteTotal: number
  items: ResourceInventorySummary[]
}

export interface OrganizationTrayInventoriesState {
  isLoading: boolean
  remoteTotal: number
  items: TrayInventorySummary[]
}

export interface OrganizationSeedInventoriesState {
  isLoading: boolean
  remoteTotal: number
  items: SeedInventorySummary[]
}

export interface SeedBatchesState extends EntityState<
SeedBatch
> {
  isLoading: boolean
}

export interface OrganizationsState extends EntityState<
Organization
> {
  isLoading: boolean
  remoteTotal: number
  resourceInventories: OrganizationResourceInventoriesState
  trayInventories: OrganizationTrayInventoriesState
  seedInventories: OrganizationSeedInventoriesState
  seedBatches: SeedBatchesState
}

export const initialState: OrganizationsState = organizationEntityAdapter.getInitialState({
  isLoading: false,
  remoteTotal: 0,
  resourceInventories: {
    isLoading: false,
    remoteTotal: 0,
    items: [],
  },
  trayInventories: {
    isLoading: false,
    remoteTotal: 0,
    items: [],
  },
  seedInventories: {
    isLoading: false,
    remoteTotal: 0,
    items: [],
  },
  seedBatches: batchesEntityAdapter.getInitialState({
    isLoading: false,
  }),
});

// Organizations resources
export const createOrganizationResourceInputAction = createAsyncThunk(
  'organizations/resources/input',
  async (payload: CreateOrganizationResourceInputData) => {
    const entryData = await apiClient.organization.createOrganizationResourceInput(payload);
    return entryData;
  },
);

export const createOrganizationResourceOutputAction = createAsyncThunk(
  'organizations/resources/output',
  async (payload: CreateOrganizationResourceOutputData) => {
    const entryData = await apiClient.organization.createOrganizationResourceOutput(payload);
    return entryData;
  },
);

export const createOrganizationResourceTransferAction = createAsyncThunk(
  'organizations/resources/transfer',
  async (payload: CreateOrganizationResourceTransferData) => {
    const entryData = await apiClient.organization.createOrganizationResourceTransfer(payload);
    return entryData;
  },
);

export const getOrganizationResourceInventoriesListAction = createAsyncThunk(
  'organizations/resources/list',
  async (payload: GetOrganizationResourceInventorySummaryList) => {
    const response = await apiClient.organization.getResourceInventorySummaryList(payload);

    return response;
  },
);

// Organizations trays
export const createOrganizationTrayInputAction = createAsyncThunk(
  'organizations/trays/input',
  async (payload: CreateOrganizationTrayInputData) => {
    const entryData = await apiClient.organization.createOrganizationTrayInput(payload);
    return entryData;
  },
);

export const createOrganizationTrayOutputAction = createAsyncThunk(
  'organizations/trays/output',
  async (payload: CreateOrganizationTrayOutputData) => {
    const entryData = await apiClient.organization.createOrganizationTrayOutput(payload);
    return entryData;
  },
);

export const createOrganizationTrayTransferAction = createAsyncThunk(
  'organizations/trays/transfer',
  async (payload: CreateOrganizationTrayTransferData) => {
    const entryData = await apiClient.organization.createOrganizationTrayTransfer(payload);
    return entryData;
  },
);

export const getOrganizationTrayInventoriesListAction = createAsyncThunk(
  'organizations/trays/list',
  async (payload: GetOrganizationTrayInventorySummaryList) => {
    const response = await apiClient.organization.getTrayInventorySummaryList(payload);

    return response;
  },
);

// Organizations seeds
export const createOrganizationVarietyInputAction = createAsyncThunk(
  'organizations/varieties/input',
  async (payload: CreateOrganizationVarietyInputData) => {
    const entryData = await apiClient.organization.createOrganizationVarietyInput(payload);
    return entryData;
  },
);

export const createOrganizationVarietyOutputAction = createAsyncThunk(
  'organizations/varieties/output',
  async (payload: CreateOrganizationVarietyOutputData) => {
    const entryData = await apiClient.organization.createOrganizationVarietyOutput(payload);
    return entryData;
  },
);

export const createOrganizationVarietyTransferAction = createAsyncThunk(
  'organizations/varieties/transfer',
  async (payload: CreateOrganizationVarietyTransferData) => {
    const entryData = await apiClient.organization.createOrganizationVarietyTransfer(payload);
    return entryData;
  },
);

export const getOrganizationSeedBatchesListAction = createAsyncThunk(
  'organizations/varieties/batches',
  async (payload: GetOrganizationSeedBatchesList) => {
    const response = await apiClient.organization.getSeedBatchesList(payload);

    return response;
  },
);

export const getOrganizationSeedInventoriesListAction = createAsyncThunk(
  'organizations/varieties/list',
  async (payload: GetOrganizationSeedInventorySummaryList) => {
    const response = await apiClient.organization.getSeedInventorySummaryList(payload);

    return response;
  },
);

export const organizationsSlice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    cleanOrganizationsAction: organizationEntityAdapter.removeAll,
    cleanOrganizationResourcesAction: (state) => {
      state.resourceInventories.items = [];
    },
    cleanOrganizationTraysAction: (state) => {
      state.trayInventories.items = [];
    },
    cleanOrganizationSeedsAction: (state) => {
      state.seedInventories.items = [];
    },
    cleanOrganizationSeedBatchesAction: (state) => {
      batchesEntityAdapter.removeAll(state.seedBatches);
    },
  },
  extraReducers: (builder) => {
    // Organizations
    // builder.addCase(getOrganizationsListAction.pending, (state) => {
    //   state.isLoading = true;
    // });
    // builder.addCase(getOrganizationsListAction.fulfilled, (state, action) => {
    //   organizationEntityAdapter.removeAll(state);
    //   organizationEntityAdapter.addMany(state, action.payload.data);
    //   state.remoteTotal = action.payload.pagination.total;
    //   state.isLoading = false;
    // });
    // builder.addCase(getOrganizationsListAction.rejected, (state) => {
    //   state.isLoading = false;
    // });

    // Resources
    builder.addCase(getOrganizationResourceInventoriesListAction.pending, (state) => {
      state.resourceInventories.isLoading = true;
    });
    builder.addCase(getOrganizationResourceInventoriesListAction.fulfilled, (state, action) => {
      // resourceInventoryEntityAdapter.removeAll(state.resourceInventories);
      // resourceInventoryEntityAdapter.addMany(state.resourceInventories, action.payload.data);
      state.resourceInventories.items = action.payload.data;
      state.resourceInventories.remoteTotal = action.payload.pagination.total;
      state.resourceInventories.isLoading = false;
    });
    builder.addCase(getOrganizationResourceInventoriesListAction.rejected, (state) => {
      state.resourceInventories.isLoading = false;
    });

    // Trays
    builder.addCase(getOrganizationTrayInventoriesListAction.pending, (state) => {
      state.trayInventories.isLoading = true;
    });
    builder.addCase(getOrganizationTrayInventoriesListAction.fulfilled, (state, action) => {
      state.trayInventories.items = action.payload.data;
      state.trayInventories.remoteTotal = action.payload.pagination.total;
      state.trayInventories.isLoading = false;
    });
    builder.addCase(getOrganizationTrayInventoriesListAction.rejected, (state) => {
      state.trayInventories.isLoading = false;
    });

    // Seeds
    builder.addCase(getOrganizationSeedInventoriesListAction.pending, (state) => {
      state.seedInventories.isLoading = true;
    });
    builder.addCase(getOrganizationSeedInventoriesListAction.fulfilled, (state, action) => {
      state.seedInventories.items = action.payload.data;
      state.seedInventories.remoteTotal = action.payload.pagination.total;
      state.seedInventories.isLoading = false;
    });
    builder.addCase(getOrganizationSeedInventoriesListAction.rejected, (state) => {
      state.seedInventories.isLoading = false;
    });

    builder.addCase(getOrganizationSeedBatchesListAction.pending, (state) => {
      state.seedBatches.isLoading = true;
    });
    builder.addCase(getOrganizationSeedBatchesListAction.fulfilled, (state, action) => {
      batchesEntityAdapter.removeAll(state.seedBatches);
      batchesEntityAdapter.addMany(state.seedBatches, action.payload);
      state.seedBatches.isLoading = false;
    });
    builder.addCase(getOrganizationSeedBatchesListAction.rejected, (state) => {
      state.seedBatches.isLoading = false;
    });
  },
});

export const {
  cleanOrganizationsAction,
  cleanOrganizationResourcesAction,
  cleanOrganizationTraysAction,
  cleanOrganizationSeedsAction,
  cleanOrganizationSeedBatchesAction,
} = organizationsSlice.actions;

// Organizations

export const isLoadingOrganizationsSelector = (state: RootState):
  boolean => state.organizations.isLoading;

export const organizationsEntitiesSelector = (state: RootState):
  Dictionary<Organization> => state.organizations.entities;

export const organizationsIdsSelector = (state: RootState):
  EntityId[] => state.organizations.ids;

export const remoteTotalSelector = (state: RootState):
  number => state.organizations.remoteTotal;

// Resources

export const isLoadingOrganizationResourcesSelector = (state: RootState):
  boolean => state.organizations.resourceInventories.isLoading;

// export const organizationResourcesEntitiesSelector = (state: RootState):
//   Dictionary<ResourceInventorySummary> => state.organizations.resourceInventories.entities;
export const organizationResourcesItemsSelector = (state: RootState):
  ResourceInventorySummary[] => state.organizations.resourceInventories.items;

// export const organizationResourcesIdsSelector = (state: RootState):
//   EntityId[] => state.organizations.resourceInventories.ids;

export const organizationResourcesRemoteTotalSelector = (state: RootState):
  number => state.organizations.resourceInventories.remoteTotal;

// Trays

export const isLoadingOrganizationTraysSelector = (state: RootState):
  boolean => state.organizations.trayInventories.isLoading;

export const organizationTraysItemsSelector = (state: RootState):
  TrayInventorySummary[] => state.organizations.trayInventories.items;

export const organizationTraysRemoteTotalSelector = (state: RootState):
  number => state.organizations.trayInventories.remoteTotal;

// Seeds

export const isLoadingOrganizationSeedsSelector = (state: RootState):
  boolean => state.organizations.seedInventories.isLoading;

export const organizationSeedsItemsSelector = (state: RootState):
  SeedInventorySummary[] => state.organizations.seedInventories.items;

export const organizationSeedsRemoteTotalSelector = (state: RootState):
  number => state.organizations.seedInventories.remoteTotal;

export const isLoadingOrganizationSeedBatchesSelector = (state: RootState):
  boolean => state.organizations.seedBatches.isLoading;

export const organizationSeedBatchesEntitiesSelector = (state: RootState):
  Dictionary<SeedBatch> => state.organizations.seedBatches.entities;

export const organizationSeedBatchesIdsSelector = (state: RootState):
  EntityId[] => state.organizations.seedBatches.ids;
